import { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SlideshowOutlinedIcon from "@mui/icons-material/SlideshowOutlined";
import DraftCardShell, { outlineButtonSx, primaryButtonSx, textButtonSx } from "../drafts/DraftCardShell";
import SlideCard from "../drafts/SlideCard";
import LibraryPicker from "../drafts/LibraryPicker";
import { isMediaSlide } from "../drafts/presentationDraftOps";

/**
 * In-chat presentation workspace. Every edit changes the draft only; nothing is
 * created until "Make presentation". Removing a slide never deletes the Microlab
 * library slide or 3D model it points to.
 */
export default function PresentationDraftCard({ pending, actions, confirming, disabled }) {
  const [expanded, setExpanded] = useState(true);
  const [showLibrary, setShowLibrary] = useState(false);
  const [editNewId, setEditNewId] = useState(null);

  if (!pending?.slides?.length) return null;
  const slides = pending.slides;
  const mediaCount = slides.filter(isMediaSlide).length;
  const busy = disabled || confirming;

  // A new slide opens straight into its editor.
  const addAfter = (index) => {
    const id = actions.addAfter(index);
    if (id) setEditNewId(id);
  };

  const slideActions = { ...actions, addAfter };

  return (
    <DraftCardShell
      kicker="PRESENTATION DRAFT"
      title={pending.presentation_title || pending.topic || "Untitled presentation"}
      summary={`${slides.length} slide${slides.length === 1 ? "" : "s"}${mediaCount ? ` · ${mediaCount} library/3D` : ""} · ${
        pending.route_mode === "apply_to_current" ? "adds to the open presentation" : "creates a new presentation"
      }`}
      icon={<SlideshowOutlinedIcon fontSize="small" />}
      expanded={expanded}
      onToggle={() => setExpanded((v) => !v)}
      footer={
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Button size="small" variant="contained" disableElevation disabled={busy} onClick={actions.confirm} sx={primaryButtonSx}>
            {confirming ? "Creating…" : "Make presentation"}
          </Button>
          <Button size="small" variant="outlined" disabled={busy} onClick={actions.discard} sx={outlineButtonSx}>
            Discard
          </Button>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 10.5, color: "#64748b", textAlign: "right" }}>Nothing is created until you approve.</Typography>
        </Stack>
      }
    >
      <Stack spacing={0.75} sx={{ maxHeight: 460, overflowY: "auto", pr: 0.25 }}>
        {slides.map((slide, index) => (
          <SlideCard
            key={slide.id || `slide-${index}`}
            slide={slide}
            index={index}
            total={slides.length}
            disabled={busy}
            actions={slideActions}
            startEditing={Boolean(editNewId) && slide.id === editNewId}
          />
        ))}
      </Stack>

      <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
        <Button size="small" startIcon={<AddRoundedIcon fontSize="small" />} disabled={busy} onClick={() => addAfter(slides.length - 1)} sx={textButtonSx}>
          Add slide
        </Button>
        <Button size="small" disabled={busy} onClick={() => setShowLibrary((v) => !v)} sx={textButtonSx}>
          {showLibrary ? "Hide library" : "Add from library"}
        </Button>
      </Stack>
      {showLibrary ? (
        <Box sx={{ mt: 0.5 }}>
          <LibraryPicker
            disabled={busy}
            onSearch={actions.search}
            actions={[{ label: "Add as slide", onPick: (item) => actions.addMedia(item) }]}
          />
        </Box>
      ) : null}
    </DraftCardShell>
  );
}

PresentationDraftCard.propTypes = {
  pending: PropTypes.object,
  actions: PropTypes.shape({
    confirm: PropTypes.func.isRequired,
    discard: PropTypes.func.isRequired,
    update: PropTypes.func.isRequired,
    remove: PropTypes.func.isRequired,
    duplicate: PropTypes.func.isRequired,
    move: PropTypes.func.isRequired,
    addAfter: PropTypes.func.isRequired,
    addMedia: PropTypes.func.isRequired,
    setVisual: PropTypes.func.isRequired,
    clearImage: PropTypes.func.isRequired,
    clear3d: PropTypes.func.isRequired,
    textOnly: PropTypes.func.isRequired,
    rewrite: PropTypes.func.isRequired,
    search: PropTypes.func.isRequired,
  }).isRequired,
  confirming: PropTypes.bool,
  disabled: PropTypes.bool,
};
